import { NO, UIColor, UIRoute, UITextView, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHProcurement, RHProcurementPublicationVisibility, RHProcurementStatus } from "../../../models/prodModels"
import { RHCore } from "../Core/RHCore"
import { RHProcurementStore } from "../Core/RHProcurementStore"
import type { CBColumnDescriptor } from "../Custom components/CB/CBDataView"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { CellView } from "../Custom components/CellView"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView, RHNumberSorting } from "../Custom components/RH/RHDataView"
import { RHFavoriteColumn } from "../Custom components/RH/RHFavoriteColumn"
import { RHFilterChipBarView } from "../Custom components/RH/RHFilterChipBarView"
import { RHFilterChipButton } from "../Custom components/RH/RHFilterChipButton"
import { RHFilterSelection } from "../Custom components/RH/RHFilterSelection"
import { RHPublishedProcurementSearch } from "../../../models/PublishedProcurementSearch"
import {
    RHDateRangeCriterion,
    RHFilterCriteria,
    RHFilterPanelView,
    RHRangeCriterion,
    RHTextCriterion
} from "../Custom components/RH/RHFilterPanelView"
import { RHNotificationBadge } from "../Custom components/RH/RHNotificationBadge"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { formatRHDateTime, formatRHDateTimeWithLineBreak } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHBadges, RHColors, RHTables } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementDeadlineResolutionTrigger } from "../Custom components/RH/Utils_slop/RHProcurementDeadlineResolutionTrigger"
import { RHProcurementLifecyclePresentation } from "../Custom components/RH/Utils_slop/RHProcurementLifecyclePresentation"
import { labelForRHViewport } from "../Custom components/RH/Utils_slop/RHResponsiveText"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHTimestampSorting } from "../Custom components/RH/Utils_slop/RHTimestampSorting"
import {
    RHMyOngoingProcurementsViewTokens,
    RHResponsiveTextTokens
} from "../Custom components/RH/Utils_slop/RHViewTokens"
import { NewProcurementViewController } from "../NewProcurement/NewProcurementViewController"
import { ProcurementDetailsViewController } from "../ProcurementDetails/ProcurementDetailsViewController"
import type { RHProcurementExcelExportButtonState } from "../ProcurementExcelExport/ProcurementExcelExportButtonView"
import { ProcurementExcelExportButtonView } from "../ProcurementExcelExport/ProcurementExcelExportButtonView"
import { ProcurementExcelExportDownloader } from "../ProcurementExcelExport/ProcurementExcelExportDownloader"
import type {
    RHProcurementExcelExport,
    RHProcurementLifecycleState,
    RHProcurerDashboardProcurement
} from "../SocketClientFunctions"
import { populatedValueOfReference } from "../Core/RHReference"


/**
 * Status filter chip keys for this table. "lopetatud" is the broad "Lõpetatud"
 * chip covering both contract sub-states; every other key names its matching
 * RHProcurementLifecycleState directly (see _lifecycleStatesForStatusChipKey).
 */
type MyOngoingProcurementsStatusChipKey =
    | "preparing"
    | "started"
    | "evaluating"
    | "lopetatud"
    | "awaitingContract"
    | "closedWithoutContract"
    | "inExecution"
    | "executed"
    | "cancelled"


type MyOngoingProcurementsQuickFilterKey =
    | MyOngoingProcurementsStatusChipKey
    | RHProcurementPublicationVisibility
    | "responsible"
    | "favorites"


// A dashboard procurement as the table holds it: the published time also kept as an ISO string the table sorts by.
type RHProcurerDashboardRow = Omit<RHProcurerDashboardProcurement, "reconciliationRounds"> & {
    _rhPublishedAtSortKey?: string
    reconciliationRounds?: RHProcurerDashboardRow[]
}


export class MyOngoingProcurementsViewController extends UIViewController {
    
    
    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header4
    ).configuredWithObject({
        text: "Minu hanked",
        textColor: RHColors.textPrimary,
        backgroundColor: UIColor.transparentColor
    }).addedAsSubviewToView(this.view)
    
    // ── Status filter chips (flat, multi-select; wrap via RHFilterChipBarView) ──
    // Hanke seisundid ja filtrid v3.1: every hankija base state. "Lõpetatud"
    // is both a broad chip (matches either contract sub-state) and refined by
    // its two narrower siblings, "Lepingu ootel" / "Lõpetatud lepinguta" —
    // see _statusChipDefinitions.
    readonly filterAllButton = MyOngoingProcurementsViewController._makeFilterChip("Kõik")
    readonly filterPublicButton = MyOngoingProcurementsViewController._makeFilterChip("Avalikud")
    readonly filterDirectedButton = MyOngoingProcurementsViewController._makeFilterChip("Mitteavalikud")
    readonly filterResponsibleButton = MyOngoingProcurementsViewController._makeFilterChip("Olen vastutav")
    readonly filterPreparingButton = MyOngoingProcurementsViewController._makeFilterChip("Ettevalmistamisel")
    readonly filterStartedButton = MyOngoingProcurementsViewController._makeFilterChip("Alustatud")
    readonly filterEvaluatingButton = MyOngoingProcurementsViewController._makeFilterChip("Hindamisel")
    readonly filterFinalizedButton = MyOngoingProcurementsViewController._makeFilterChip("Lõpetatud")
    readonly filterAwaitingContractButton = MyOngoingProcurementsViewController._makeFilterChip("Lepingu ootel")
    readonly filterClosedWithoutContractButton = MyOngoingProcurementsViewController._makeFilterChip("Lõpetatud lepinguta")
    readonly filterInExecutionButton = MyOngoingProcurementsViewController._makeFilterChip("Täitmisel")
    readonly filterExecutedButton = MyOngoingProcurementsViewController._makeFilterChip("Teostatud")
    readonly filterCancelledButton = MyOngoingProcurementsViewController._makeFilterChip(
        RHProcurementLifecyclePresentation.textForLifecycleState("cancelled")
    )
    
    // Last chip — opens the "Muud filtrid" panel below (usesExternalToggle hides
    // the panel's own toggle); the funnel + caret and neutral "soft" open look come
    // from the chip itself.
    readonly moreFiltersButton = MyOngoingProcurementsViewController._makeFilterChip("Muud filtrid")
    
    readonly filterFavoritesButton = MyOngoingProcurementsViewController._makeFilterChip("Lemmikud")

    // Cross-company facet. It remains visible but disabled until multiple company
    // memberships are available. Toggling it re-fetches from every company the
    // user belongs to instead of just the active one.
    readonly filterAllCompaniesButton = MyOngoingProcurementsViewController
        ._makeFilterChip("Kõik minu ettevõtted")
        .configuredWithObject({ enabled: NO })

    // The wrapping chip bar — owns + lays out all status chips plus "Muud filtrid".
    readonly filterChipBar = new RHFilterChipBarView(this.view.elementID + "FilterChipBar")
        .addedAsSubviewToView(this.view)
    
    // Reusable inline filter panel. Emits an RHFilterCriteria the controller applies
    // client-side today (and can hand to the server unchanged once pagination lands).
    readonly filterPanel = new RHFilterPanelView(this.view.elementID + "FilterPanel")
        .configuredWithObject({
            usesExternalToggle: YES,
            fields: [
                {
                    key: "procuringCompanyName", label: "Hankija", kind: "text",
                    autocompleteStringsProvider: () => this._allProcurements.map(procurement =>
                        this._procuringCompanyNameForProcurement(procurement)
                    )
                },
                { key: "responsibleProcuringUser", label: "Hanke eest vastutav isik", kind: "text" },
                { key: "estimatedCost", label: "Eeldatav maksumus (KM-ta)", kind: "numberRange", allowDecimals: YES },
                { key: "publishedAt", label: "Avaldatud", kind: "dateRange" },
                { key: "deadlineDate", label: "Tähtaeg", kind: "dateRange" },
                {
                    key: "registeredTenderers", label: "Registreerunud pakkujaid",
                    kind: "numberRange", fromPlaceholder: "Min", toPlaceholder: "Max"
                },
                {
                    key: "offerCount", label: "Pakkumusi",
                    kind: "numberRange", fromPlaceholder: "Min", toPlaceholder: "Max"
                }
            ]
        }).addedAsSubviewToView(this.view)
    
    readonly procurementsDataView = new RHDataView<RHProcurerDashboardRow>().configuredWithObject({
        additionalSearchTextForDataItem: procurement =>
            RHPublishedProcurementSearch.searchableTextForResponsibleProcuringUserOfProcurement(procurement),
        pageScrollForwardingMode: "revealThenBlockPage",
        treeDisclosureColumnKeyPath: "referenceNumber",
        descriptors: this._buildDescriptors()
    }).addedAsSubviewToView(this.view)

    _buildDescriptors(): CBColumnDescriptor<RHProcurerDashboardRow>[] {
        return [
            RHFavoriteColumn.descriptorWithProcurementIdProvider<RHProcurerDashboardRow>(
                dataItem => dataItem._id?.toString()
            ),
            CBDataViewCellDescriptorWithObject({
                title: labelForRHViewport(RHResponsiveTextTokens.tableHeaderLabels.referenceNumber),
                keyPath: "referenceNumber",
                weight: 1.5,
                alignment: UITextView.textAlignment.center,
                allowSorting: YES,
                updateCell: (cellView, dataItem) => {
                    cellView.text = dataItem.referenceNumber || "-"
                }
            }),
            // "Avamise protokoll" is hidden in this (dashboard "Avaldatud") view per
            // Rene's request. The descriptor is kept rather than deleted so the
            // Excel-export wiring survives and the column can be restored by flipping
            // `hidden` — the same reversible pattern the architect uses for the
            // sorting-only descriptor in PublishedProcurementsViewController.
            CBDataViewCellDescriptorWithObject<RHProcurement, ProcurementExcelExportButtonView>({
                title: "Avamise protokoll",
                keyPath: "_id",
                weight: 2.0,
                alignment: UITextView.textAlignment.center,
                hidden: YES,
                allowSorting: NO,
                initCustomView: () => new ProcurementExcelExportButtonView(),
                updateCell: (cellView, dataItem) => {
                    this._configureExcelExportCell(cellView.customView, dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Nimetus",
                keyPath: "title",
                buttonWasPressed: (_cellView, dataItem) => this._openProcurementForManagement(dataItem),
                initCell: cellView => cellView.configureAsTextLinkWithNormalTextColor(RHColor.textBlack),
                weight: 3.0,
                minimumWidth: RHMyOngoingProcurementsViewTokens.columnMinimumWidths.title,
                alignment: UITextView.textAlignment.left,
                allowSorting: YES,
                updateCell: (cellView, dataItem) => {
                    cellView.text = RHTextSanitizer.escapedHTML(dataItem.title || "Nimetus puudub")
                    // Long titles wrap to (and clamp at) two lines so the row keeps the
                    // table's two-line rhythm; the full title shows in the hover tooltip.
                    cellView.configureAsClampedMultilineText()
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Hankija",
                keyPath: "procuringCompany.name",
                weight: RHMyOngoingProcurementsViewTokens.companyColumnWeight,
                alignment: UITextView.textAlignment.left,
                allowSorting: YES,
                updateCell: (cellView, dataItem) => {
                    cellView.text = populatedValueOfReference(dataItem.procuringCompany)?.name || "-"
                }
            }),
            // "CPV-kood" is hidden in this (dashboard "Avaldatud") view per Rene's
            // request — same reversible `hidden` descriptor pattern as "Avamise
            // protokoll" above. ("Registreerunud pakkujaid" used to sit here; it was
            // moved next to "Pakkumusi" further down.)
            CBDataViewCellDescriptorWithObject({
                title: "CPV-kood",
                keyPath: "parts",
                weight: 1.2,
                alignment: UITextView.textAlignment.left,
                hidden: YES,
                allowSorting: NO,
                updateCell: (cellView, dataItem) => {
                    const parts = dataItem.parts || []
                    if (parts.length === 1) {
                        cellView.text = parts[0].CPV || "-"
                    }
                    else if (parts.length > 1) {
                        const cpvCodes = parts.map(p => p.CPV).filter(Boolean)
                        cellView.text = cpvCodes.length > 0
                                        ? `${cpvCodes[0]} (+${cpvCodes.length - 1})`
                                        : `${parts.length} osa`
                    }
                    else {
                        cellView.text = "-"
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: labelForRHViewport(RHResponsiveTextTokens.tableHeaderLabels.estimatedCost),
                keyPath: RHNumberSorting.valueKeyPath,
                weight: 1.2,
                alignment: UITextView.textAlignment.right,
                allowSorting: YES,
                dataType: "number",
                sortingInstructions: orderingState => RHNumberSorting.instructions(orderingState),
                // Monetary amounts read best right-aligned (Rene), header follows the content.
                updateCell: (cellView, dataItem) => {
                    const parts = dataItem.parts || []
                    const costs = parts.map(p => p.estimatedCost).filter((c): c is number => typeof c === "number")
                    if (costs.length === 0) {
                        cellView.text = "-"
                    }
                    else {
                        const total = costs.reduce((sum, c) => sum + c, 0)
                        cellView.text = RHEuroFormatter.formattedAmount(total)
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: labelForRHViewport(RHResponsiveTextTokens.tableHeaderLabels.published),
                keyPath: "_rhPublishedAtSortKey",
                weight: 1.2,
                alignment: UITextView.textAlignment.center,
                allowSorting: YES,
                initialOrderingState: "descending",
                dataType: "string",
                updateCell: (cellView, dataItem) => {
                    if (dataItem.publishedAt) {
                        const date = new Date(dataItem.publishedAt)
                        cellView.text = formatRHDateTimeWithLineBreak(date)
                        cellView.titleLabel!.isSingleLine = NO
                    }
                    else {
                        cellView.text = "-"
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Tähtaeg",
                keyPath: "deadlineDate",
                weight: 1.15,
                alignment: UITextView.textAlignment.center,
                allowSorting: YES,
                // No initialOrderingState here: "Avaldatud" owns the initial order.
                // A second initially-ordered column would act as the tie-breaker for
                // equal publish times, overriding the server's createdAt tie-break,
                // and would render a second sort caret on load.
                dataType: "string",
                updateCell: (cellView, dataItem) => {
                    if (dataItem.deadlineDate) {
                        const date = new Date(dataItem.deadlineDate)
                        const now = new Date()
                        const isExpired = date < now
                        
                        cellView.text = formatRHDateTimeWithLineBreak(date)
                        cellView.titleLabel!.isSingleLine = NO
                        
                        if (isExpired) {
                            cellView.titleLabel!.textColor = RHColor.textMuted
                        }
                        else {
                            cellView.titleLabel!.textColor = RHColor.textBlack
                        }
                    }
                    else {
                        cellView.text = "-"
                        cellView.titleLabel!.textColor = RHColor.textBlack
                    }
                }
            }),
            // Moved here (immediately before "Pakkumusi") per Rene's request — the
            // registered-tenderer count reads naturally next to the offer count.
            CBDataViewCellDescriptorWithObject({
                title: labelForRHViewport(RHResponsiveTextTokens.tableHeaderLabels.registeredTenderers),
                keyPath: "registeredTendererCount",
                weight: 1.05,
                alignment: UITextView.textAlignment.center,
                allowSorting: YES,
                dataType: "number",
                updateCell: (cellView, dataItem) => {
                    const count = this._registeredTendererCountForProcurement(dataItem)
                    cellView.text = count.toString()
                    this._configureCountAffordance(cellView, count > 0)
                },
                buttonWasPressed: (_cellView, dataItem, _event) => {
                    const count = this._registeredTendererCountForProcurement(dataItem)
                    if (count === 0) {
                        return
                    }
                    this._openOffersForProcurement(dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: labelForRHViewport(RHResponsiveTextTokens.tableHeaderLabels.offerCount),
                keyPath: "offerCount",
                weight: 1.5,
                alignment: UITextView.textAlignment.center,
                allowSorting: YES,
                dataType: "number",
                // Hovering the count lists who submitted an offer and when. The shared
                // RHTableRowView renders this through the RH tooltip.
                tooltipText: (dataItem) => {
                    const procurementId = dataItem._id?.toString() || ""
                    const count = this._offerCountForProcurement(dataItem)
                    if (count > 0) {
                        return this._offerTooltipTextForProcurement(procurementId)
                    }
                    return ""
                },
                updateCell: (cellView, dataItem) => {
                    const count = this._offerCountForProcurement(dataItem)
                    cellView.text = count.toString()
                    
                    // When there are offers the count becomes a clickable affordance
                    // (dotted underline + pointer cursor) that opens the offers tab, the
                    // same destination as the "Vaata pakkumusi" status link. The hover
                    // tooltip still lists who submitted and when.
                    this._configureCountAffordance(cellView, count > 0)
                },
                // Clicking a non-zero count opens the procurement's "Pakkumused" tab —
                // the exact route the "Vaata pakkumusi" status cell builds (tab: "offers").
                buttonWasPressed: (_cellView, dataItem, _event) => {
                    const count = this._offerCountForProcurement(dataItem)
                    if (count === 0) {
                        return
                    }
                    this._openOffersForProcurement(dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Seisund",
                keyPath: "status",
                weight: 1.9,
                minimumWidth: RHMyOngoingProcurementsViewTokens.columnMinimumWidths.status,
                alignment: UITextView.textAlignment.center,
                allowSorting: YES,
                updateCell: (cellView, dataItem) => {
                    const presentation = this._statusPresentationForProcurement(dataItem)
                    this._configureStatusCell(
                        cellView,
                        RHProcurementLifecyclePresentation.twoLineCellHTML(
                            presentation.mainText,
                            presentation.secondaryText,
                            presentation.secondaryColor
                        ),
                        presentation.color,
                        presentation.linksToOffers
                    )
                },
                buttonWasPressed: (_cellView, dataItem, _event) => {
                    if (!this._canOpenOffersFromStatusCell(dataItem)) {
                        return
                    }
                    this._openOffersForProcurement(dataItem)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Haldus",
                keyPath: "_id",
                weight: 1,
                minimumWidth: RHMyOngoingProcurementsViewTokens.columnMinimumWidths.action,
                alignment: UITextView.textAlignment.center,
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new RHTableActionButtonView().configuredWithObject({
                    preferredButtonWidth: 116,
                    preferredButtonHeight: 32
                }),
                updateCell: (cellView, dataItem) => {
                    
                    // Bold = unread information; orange dot = unfinished action.
                    const requiresQA = dataItem.requiresProcurerQAAttention === YES
                    const hasUnreadMessage = RHCore.sharedInstance.notificationStore.offerMessageCount(
                        dataItem._id?.toString() ?? ""
                    ) > 0
                    const requiresEvaluation = dataItem.requiresProcurerAttention === YES
                    const hasUnreadNotification = (dataItem.unseenNotificationCount ?? 0) > 0
                    let unreadTooltip = "Lugemata hankega seotud teavitus."
                    if (hasUnreadMessage) {
                        unreadTooltip = "Lugemata sõnum."
                    }
                    let actionTooltip = "Pakkumused ootavad hindamist."
                    if (requiresQA) {
                        actionTooltip = "Vastamata küsimus ootab vastust."
                    }
                    if (requiresEvaluation && requiresQA) {
                        actionTooltip = "Pakkumused ootavad hindamist ja küsimus vastust."
                    }
                    cellView.customView.text = "Vaata"
                    if (dataItem.status === "draft") {
                        cellView.customView.text = "Jätka"
                    }
                    RHNotificationBadge.applyUnreadEmphasis(
                        cellView.customView.button.titleLabel,
                        hasUnreadNotification || hasUnreadMessage,
                        unreadTooltip
                    )
                    RHNotificationBadge.applyDot(
                        cellView.customView.button.titleLabel,
                        requiresEvaluation || requiresQA,
                        actionTooltip
                    )
                    cellView.customView.buttonWasPressed = () => {
                        this._openProcurementForManagement(dataItem)
                    }
                }
            })
        ]
    }


    _openProcurementForManagement(dataItem: RHProcurerDashboardRow) {
        const procurementId = dataItem._id?.toString()
        if (!procurementId) {
            return
        }
        const backToRouteComponentName =
            this.backToRouteComponentName ?? MyOngoingProcurementsViewController.routeComponentName

        if (dataItem.status === "draft") {
            const company = dataItem.procuringCompany
            const companyId = (company?._id ?? company)?.toString()
            if (!companyId || companyId === "[object Object]") {
                CBDialogViewShower.alert("Ettevõtte konteksti ei leitud.")
                return
            }
            const affiliationStore = RHCore.sharedInstance.companyAffiliationStore
            if (affiliationStore.activeCompanyId !== companyId) {
                affiliationStore.deactivateDefaultAllCompaniesScope()
                affiliationStore.activateCompany(companyId)
                if (affiliationStore.activeCompanyId !== companyId) {
                    CBDialogViewShower.alert("Ettevõtte konteksti vahetamine ebaõnnestus.")
                    return
                }
            }
            UIRoute.currentRoute
                .routeByRemovingComponentNamed(backToRouteComponentName)
                .routeWithViewControllerComponent(NewProcurementViewController, { id: procurementId })
                .apply()
            return
        }

        UIRoute.currentRoute
            .routeWithViewControllerComponent(
                ProcurementDetailsViewController,
                {
                    id: procurementId,
                    backTo: backToRouteComponentName
                }
            )
            .routeByRemovingComponentNamed(backToRouteComponentName)
            .apply()
    }

    _canOpenOffersFromStatusCell(dataItem: RHProcurerDashboardRow): boolean {
        return this._statusPresentationForProcurement(dataItem).linksToOffers
    }

    /**
     * Produces the "Staatus" cell content from the server-resolved lifecycle
     * state (Hanke seisundid ja filtrid v3.1) — see RHProcurementLifecyclePresentation,
     * the single source of truth shared by this table, the pakkuja table, and
     * the procurement-details header.
     */
    _statusPresentationForProcurement(dataItem: RHProcurerDashboardRow) {
        return RHProcurementLifecyclePresentation.presentationForProcurerRow(dataItem)
    }
    
    _offerCountForProcurement(dataItem: RHProcurerDashboardRow): number {
        return dataItem.offerCount
    }

    _registeredTendererCountForProcurement(dataItem: RHProcurerDashboardRow): number {
        return dataItem.registeredTendererCount
    }

    _openOffersForProcurement(dataItem: RHProcurement) {
        const procurementId = dataItem._id?.toString()
        if (!procurementId) {
            return
        }
        const backToRouteComponentName =
            this.backToRouteComponentName ?? MyOngoingProcurementsViewController.routeComponentName
        UIRoute.currentRoute
            .routeWithViewControllerComponent(
                ProcurementDetailsViewController,
                {
                    id: procurementId,
                    tab: "offers",
                    backTo: backToRouteComponentName
                }
            )
            .routeByRemovingComponentNamed(backToRouteComponentName)
            .apply()
    }
    
    /**
     * Builds the "Pakkumusi" tooltip text for a procurement: one line per
     * submitted offer, "<company> — <dd.mm.yyyy hh:mm>", ordered by submission
     * time. Returns "" when there are no cached offer details.
     */
    private _offerTooltipTextForProcurement(procurementId: string): string {
        const offers = this._offerDetailsByProcurement[procurementId] ?? []
        return offers
            .slice()
            .sort((firstOffer, secondOffer) =>
                new Date(firstOffer.submittedAt).getTime() - new Date(secondOffer.submittedAt).getTime()
            )
            .map(offer => `${offer.companyName} — ${formatRHDateTime(new Date(offer.submittedAt))}`)
            .join("\n")
    }
    
    /**
     * Configures a count cell. Registered-tenderer and offer-count columns carry a
     * `buttonWasPressed` handler,
     * so every cell is a "button" variant (CellView) — this keeps the count looking like
     * the plain black number it was, and only when there are offers turns it into a
     * clickable affordance: interactive, pointer cursor, and a dotted underline. Clicking
     * opens the offers tab; hovering still reveals who submitted them (tooltip attached
     * centrally by RHTableRowView via this column's descriptor `tooltipText`). A zero count
     * is left non-interactive with no affordance. The cell is reused across rows, so both
     * branches fully reset prior state.
     */
    _configureCountAffordance(
        cellView: CellView<RHProcurerDashboardRow>,
        isClickable: boolean
    ) {
        const button = cellView.buttonView
        if (!button) {
            return
        }
        
        // Keep the count black in every state (the button variant defaults to a blue tint)
        // and give it no button chrome, so it still reads as a plain number.
        button.colors.titleLabel = {
            normal: RHColor.textBlack,
            hovered: RHColor.textBlack,
            focused: RHColor.textBlack,
            highlighted: RHColor.textBlack,
            selected: RHColor.textBlack
        }
        button.colors.background = {
            normal: UIColor.transparentColor,
            hovered: UIColor.transparentColor,
            focused: UIColor.transparentColor,
            highlighted: UIColor.transparentColor,
            selected: UIColor.transparentColor
        }
        button.titleLabel.textAlignment = UITextView.textAlignment.center
        button.updateContentForCurrentState()
        
        // Only a non-zero count is interactive: pointer cursor + dotted underline. A zero
        // count stays inert with no affordance. Both branches fully reset prior state,
        // because the cell is reused across rows.
        if (isClickable) {
            button.userInteractionEnabled = YES
            button.configureWithObject({ style: { cursor: "pointer" } })
            button.titleLabel.textElementView.configureWithObject({
                style: {
                    textDecoration: "underline dotted",
                    textDecorationColor: RHColors.blackAlpha40.stringValue,
                    textUnderlineOffset: "3px"
                }
            })
        }
        else {
            button.userInteractionEnabled = NO
            button.configureWithObject({ style: { cursor: "default" } })
            button.titleLabel.textElementView.configureWithObject({
                style: {
                    textDecoration: "none"
                }
            })
        }
    }
    
    private _applyTableColumnLayout() {
        const cellWeights = this._currentColumnWeights()
        this.procurementsDataView.tableHeaderView.configureWithObject({
            cellWeights,
            intrinsicContentHeight: () => 64
        })
        this.procurementsDataView.tableView.configureWithObject({
            cellWeights
        })
        this._configureTableHeaderCells()
        this.procurementsDataView.tableHeaderView.setNeedsLayout()
        this.procurementsDataView.tableView.setNeedsLayout()
    }

    // Keep the Hankija weight after Nimetus, matching the visible descriptor order.
    _currentColumnWeights(): number[] {
        const baseWeights = RHMyOngoingProcurementsViewTokens.columnWeights
        return [
            ...baseWeights.slice(0, 3),
            RHMyOngoingProcurementsViewTokens.companyColumnWeight,
            ...baseWeights.slice(3)
        ]
    }

    private _configureTableHeaderCells() {
        for (const headerCell of this.procurementsDataView.tableHeaderView.cells) {
            const buttonView = headerCell.buttonView
            if (buttonView) {
                buttonView.contentPadding = 2
            }
            const titleLabel = buttonView?.titleLabel ?? headerCell.titleLabel
            if (!titleLabel) {
                continue
            }
            titleLabel.isSingleLine = NO
            titleLabel.style.whiteSpace = "normal"
            titleLabel.style.lineHeight = RHMyOngoingProcurementsViewTokens.multilineHeaderLineHeight
            titleLabel.textElementView.style.whiteSpace = "normal"
            titleLabel.textElementView.style.lineHeight = RHMyOngoingProcurementsViewTokens.multilineHeaderLineHeight
        }
    }
    
    _configureStatusCell(
        cellView: CellView<RHProcurerDashboardRow>,
        statusText: string,
        statusColor: UIColor,
        isStatusAction: boolean
    ) {
        cellView.text = statusText
        // statusText is the two-line bold+italic HTML markup — hoverText is a plain
        // tooltip, so strip the markup rather than showing raw tags.
        cellView.hoverText = undefined
        if (isStatusAction) {
            cellView.hoverText = RHTextSanitizer.plainText(statusText)
        }
        cellView.titleLabel!.textAlignment = UITextView.textAlignment.center
        cellView.titleLabel!.textColor = statusColor
        // Let the two-line status (main state + informative status, e.g. the
        // deadline countdown or "Vaata pakkumusi") wrap and show in full instead
        // of truncating. The Staatus column is widened in the column weights so
        // they fit in two lines.
        cellView.titleLabel!.configureWithObject({
            isSingleLine: NO,
            style: { whiteSpace: "normal" }
        })
        
        if (!cellView.buttonView) {
            return
        }
        
        cellView.buttonView.userInteractionEnabled = isStatusAction ? YES : NO
        cellView.buttonView.style.cursor = isStatusAction ? "pointer" : "default"
        cellView.buttonView.colors.titleLabel = {
            normal: statusColor,
            hovered: statusColor,
            focused: statusColor,
            highlighted: statusColor,
            selected: statusColor
        }
        cellView.buttonView.colors.background = {
            normal: UIColor.transparentColor,
            hovered: UIColor.transparentColor,
            focused: UIColor.transparentColor,
            highlighted: UIColor.transparentColor,
            selected: UIColor.transparentColor
        }
        cellView.buttonView.titleLabel.configureWithObject({
            isSingleLine: NO,
            style: { whiteSpace: "normal" }
        })
        cellView.buttonView.titleLabel.textAlignment = UITextView.textAlignment.center
        cellView.buttonView.updateContentForCurrentState()
    }
    
    readonly bottomSpacerView = new UIView().addedAsSubviewToView(this.view)
    
    // When this controller is hosted as the "Avaldatud" tab of the procurer
    // dashboard, the host sets this so opening a procurement returns to the
    // dashboard instead of the standalone "ongoing_procurements" route (no longer
    // in the navbar). Defaults to this controller's own route for the standalone case.
    backToRouteComponentName?: string
    
    // When embedded under the dashboard the "Töös hanked" page title is redundant
    // with the "Avaldatud" tab, so the host hides it and the table sits directly
    // under the tab row, matching the other dashboard tabs. Standalone keeps the title.
    showsPageTitle = YES
    
    // Full, unfiltered dataset kept separate from what the table shows, so the filter
    // panel can re-derive the visible subset without re-fetching (mirrors
    // PublishedProcurementsViewController._allPublishedProcurements). When server
    // pagination is introduced, this becomes "the current page" and _applyFilters
    // sends _filterCriteria to the server instead of filtering in memory.
    _allProcurements: RHProcurerDashboardRow[] = []
    _filterCriteria: RHFilterCriteria = {}
    _chipCountUpdateGeneration = 0
    readonly _quickFilters = new RHFilterSelection<MyOngoingProcurementsQuickFilterKey>(
        ["public", "directed"], ["responsible"]
    )
    // Cross-company fan-out toggle — unlike the other chips, this changes
    // _reloadData()'s data source rather than just re-filtering _allProcurements.
    _isAllCompaniesScopeActive = NO
    // Apply the all-procurer default once, preserving later local selection.
    _hasAppliedDefaultCompanyScope = NO

    _loadGeneration = 0
    _offerDetailsLoadGeneration = 0
    _loadHasFailed = NO
    _excelExportsByProcurementId: Record<string, RHProcurementExcelExport> = {}
    _excelExportLoadingProcurementIds = new Set<string>()
    
    // Cache for the "Pakkumusi" hover tooltip: per procurement id, the list of
    // (company name, submission time) for every submitted offer. Sourced from
    // RHOfferStore.getIncomingOffers() and grouped by procurement id.
    _offerDetailsByProcurement: Record<string, { companyName: string, submittedAt: string }[]> = {}

    // Periodic repaint so the "Tähtajani …" deadline countdown keeps advancing
    // while this tab is open — started in viewWillAppear, stopped in viewWillDisappear.
    static readonly _deadlineCountdownTickIntervalMilliseconds = 30_000
    _deadlineCountdownTickTimer?: ReturnType<typeof setInterval>
    readonly _deadlineResolutionTrigger = new RHProcurementDeadlineResolutionTrigger()

    constructor(view: UIView) {
        super(view)
        
        this.view.configureWithObject({
            backgroundColor: RHColor.background
        })
        
        this.filterChipBar.chips = this._visibleStatusFilterButtons()
        // noinspection JSConstantReassignment
        this.filterAllButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._clearQuickFilters()
        }
        for (const chip of this._quickFilterDefinitions()) {
            // noinspection JSConstantReassignment
            chip.button.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
                this._toggleFilter(chip.key)
            }
        }
        
        // Unlike the other chips, this changes which company scope the fetch
        // uses, so it re-fetches (_reloadData) rather than just re-filtering.
        this.filterAllCompaniesButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._isAllCompaniesScopeActive = NO == this._isAllCompaniesScopeActive
            this._updateChipsAppearance()
            this._reloadData()
        }
        
        // The "Muud filtrid" chip toggles the panel; the panel mirrors its open
        // state back. Same wiring as the other views.
        this.moreFiltersButton.titleLabel.text =
            MyOngoingProcurementsViewController._moreFiltersLabelMarkup(NO)
        // noinspection JSConstantReassignment
        this.moreFiltersButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.filterPanel.isExpanded = NO == this.filterPanel.isExpanded
        }
        this.filterPanel.isExpandedDidChange = (expanded) => {
            this.moreFiltersButton.chipOpen = expanded
            this.moreFiltersButton.titleLabel.text =
                MyOngoingProcurementsViewController._moreFiltersLabelMarkup(expanded)
            this.filterChipBar.setNeedsLayout()
            this.view.setNeedsLayout()
        }
        
        this.filterPanel.filterCriteriaDidChange = (criteria) => {
            this._filterCriteria = criteria
            this.moreFiltersButton.chipActive = Object.keys(criteria).length > 0
            this._applyFilters()
        }
        
        this.procurementsDataView.didUpdateTableAfterSearchTextChange = () => {
            void this._updateChipCounts()
            this._updateNoDataMessage()
        }
        
        this._applyTableColumnLayout()
        this._updateChipsAppearance()
    }
    
    
    private static _makeFilterChip(title: string): RHFilterChipButton {
        return new RHFilterChipButton().configuredWithObject({
            titleLabel: { text: title }
        })
    }
    
    
    // "Muud filtrid" toggle label: funnel icon + text + a caret that flips when the
    // panel is open. Rendered as HTML so the inline SVGs inherit the chip text colour.
    private static _moreFiltersLabelMarkup(expanded: boolean): string {
        let caret = RHBadges.filterChip.caretCollapsedMarkup
        if (expanded) {
            caret = RHBadges.filterChip.caretExpandedMarkup
        }
        return RHBadges.filterChip.funnelIconMarkup + "Muud filtrid" + caret
    }
    
    
    // ── Status filter chip state (multi-select; shared lifecycle classifier) ─────

    _quickFilterDefinitions(): { button: RHFilterChipButton, key: MyOngoingProcurementsQuickFilterKey, baseLabel: string }[] {
        return [
            { button: this.filterPublicButton, key: "public", baseLabel: "Avalikud" },
            { button: this.filterDirectedButton, key: "directed", baseLabel: "Mitteavalikud" },
            { button: this.filterPreparingButton, key: "preparing", baseLabel: "Ettevalmistamisel" },
            { button: this.filterStartedButton, key: "started", baseLabel: "Alustatud" },
            { button: this.filterEvaluatingButton, key: "evaluating", baseLabel: "Hindamisel" },
            { button: this.filterFinalizedButton, key: "lopetatud", baseLabel: "Lõpetatud" },
            { button: this.filterAwaitingContractButton, key: "awaitingContract", baseLabel: "Lepingu ootel" },
            {
                button: this.filterClosedWithoutContractButton,
                key: "closedWithoutContract",
                baseLabel: "Lõpetatud lepinguta"
            },
            { button: this.filterInExecutionButton, key: "inExecution", baseLabel: "Täitmisel" },
            { button: this.filterExecutedButton, key: "executed", baseLabel: "Teostatud" },
            {
                button: this.filterCancelledButton,
                key: "cancelled",
                baseLabel: RHProcurementLifecyclePresentation.textForLifecycleState("cancelled")
            },
            { button: this.filterResponsibleButton, key: "responsible", baseLabel: "Olen vastutav" },
            { button: this.filterFavoritesButton, key: "favorites", baseLabel: "Lemmikud" }
        ]
    }


    /** The lifecycle states a chip key matches — "lopetatud" is the broad chip covering both contract sub-states. */
    static _lifecycleStatesForStatusChipKey(
        key: MyOngoingProcurementsStatusChipKey
    ): RHProcurementLifecycleState[] {
        if (key === "lopetatud") {
            return ["awaitingContract", "closedWithoutContract"]
        }
        return [key]
    }


    private _visibleStatusFilterButtons() {
        const buttons = [
            this.filterAllButton,
            this.filterPublicButton,
            this.filterDirectedButton,
            this.filterPreparingButton,
            this.filterStartedButton,
            this.filterEvaluatingButton,
            this.filterFinalizedButton,
            this.filterAwaitingContractButton,
            this.filterClosedWithoutContractButton,
            this.filterInExecutionButton,
            this.filterExecutedButton,
            this.filterCancelledButton,
            this.filterResponsibleButton
        ]
        buttons.push(
            this.filterAllCompaniesButton,
            this.filterFavoritesButton,
            this.moreFiltersButton
        )
        return buttons
    }


    _toggleFilter(key: MyOngoingProcurementsQuickFilterKey) {
        this._quickFilters.toggleKey(key)
        this._updateChipsAppearance()
        this._applyFilters()
    }


    _clearQuickFilters() {
        this._quickFilters.selectedKeys.clear()
        this._updateChipsAppearance()
        this._applyFilters()
    }


    _updateChipsAppearance() {
        this.filterAllButton.chipActive = this._quickFilters.selectedKeys.size === 0
        for (const chip of this._quickFilterDefinitions()) {
            chip.button.chipActive = this._quickFilters.selectedKeys.has(chip.key)
        }
        this.filterAllCompaniesButton.chipActive = this._isAllCompaniesScopeActive
    }


    _procurementMatchesQuickFilterKey(
        procurement: RHProcurerDashboardRow,
        key: MyOngoingProcurementsQuickFilterKey
    ): boolean {
        if (key === "public" || key === "directed") {
            return (procurement.publicationVisibility ?? "public") === key
        }
        if (key === "responsible") {
            const userId = RHCore.sharedInstance.userProfile?._id?.toString()
            const responsibleUser = procurement.responsibleProcuringUser
            const responsibleUserId = (responsibleUser?._id ?? responsibleUser)?.toString()
            return !!userId && responsibleUserId === userId
        }
        if (key === "favorites") {
            return RHCore.sharedInstance.isProcurementFavorite(procurement._id?.toString() ?? "")
        }
        return procurement.lifecycleState != null &&
            MyOngoingProcurementsViewController._lifecycleStatesForStatusChipKey(key)
                .includes(procurement.lifecycleState)
    }


    async _updateChipCounts() {
        const updateGeneration = ++this._chipCountUpdateGeneration
        let procurementsMatchingCriteria = this._allProcurements.filter(
            procurement => this._procurementMatchesFilterCriteria(procurement)
        )
        if (this.procurementsDataView.searchTextField.text.trim().length > 0 ||
            this.procurementsDataView.tableHeaderView.columnFilterCriteria.length > 0) {
            procurementsMatchingCriteria = await this.procurementsDataView.dataByApplyingFiltersToData(
                procurementsMatchingCriteria
            )
            if (updateGeneration !== this._chipCountUpdateGeneration) {
                return
            }
        }
        this.filterAllButton.titleLabel.text = this._chipLabelWithCount("Kõik", procurementsMatchingCriteria.length)
        for (const chip of this._quickFilterDefinitions()) {
            const selection = this._quickFilters.selectionForCountingKey(chip.key)
            const count = procurementsMatchingCriteria.filter(procurement => selection.doesValueMatchSelection(
                procurement, (value, key) => this._procurementMatchesQuickFilterKey(value, key)
            )).length
            chip.button.titleLabel.text = this._chipLabelWithCount(chip.baseLabel, count)
        }
        this.filterChipBar.setNeedsLayout()
        this.view.setNeedsLayout()
    }


    // Count appended as PLAIN text (no HTML span — see RHFilterChipButton sizing).
    private _chipLabelWithCount(label: string, count: number): string {
        return `${label} · ${count}`
    }
    
    
    static override readonly routeComponentName = "ongoing_procurements"
    static override readonly ParameterIdentifierName = {}
    static readonly requiredApplicationModes: ("procurer" | "offerer")[] = ["procurer"]
    
    override async viewWillAppear() {
        await super.viewWillAppear()
        RHCore.sharedInstance.procurementStore.subscribe(this, () => {
            this._reloadData()
        })
        RHCore.sharedInstance.notificationStore.subscribe(this, () => {
            this.procurementsDataView.tableView.reloadData()
        })
        RHCore.sharedInstance.offerStore.subscribe(this, () => {
            this._reloadOfferDetails()
        })
        // Repaints the visible rows (no re-fetch) so the "Tähtajani …" deadline
        // countdown keeps advancing while the tab stays open, per spec: "loendur
        // uueneb ajas". Every 30s keeps the minute-granularity text from ever
        // drifting more than half a minute stale.
        this._deadlineCountdownTickTimer = setInterval(() => {
            this._requestResolutionForExpiredDeadlineCountdowns()
            this.procurementsDataView.tableView.reloadData()
        }, MyOngoingProcurementsViewController._deadlineCountdownTickIntervalMilliseconds)
        await this._refreshCompanyScopeAffordances()
        await this._reloadData()
    }

    // Defaults to every own procurer and disables the scope chip when there is only one.
    async _refreshCompanyScopeAffordances() {
        const affiliations = await RHCore.sharedInstance.companyAffiliationStore.retrieveMyCompanyAffiliations()
        const canActAsProcurerForMultipleCompanies = affiliations.filter(affiliation =>
            affiliation.membershipStatus === "active" && affiliation.source !== "relationship" &&
            RHCore.userCanActAsProcurerThroughAffiliation(affiliation)
        ).length > 1
        if (!this._hasAppliedDefaultCompanyScope || !canActAsProcurerForMultipleCompanies) {
            this._hasAppliedDefaultCompanyScope = YES
            this._isAllCompaniesScopeActive = canActAsProcurerForMultipleCompanies
            this._updateChipsAppearance()
        }
        this.filterAllCompaniesButton.enabled = canActAsProcurerForMultipleCompanies
        this.filterChipBar.chips = this._visibleStatusFilterButtons()
        this.filterChipBar.setNeedsLayout()
        this.view.setNeedsLayout()
    }
    
    override async viewWillDisappear() {
        await super.viewWillDisappear()
        this._loadGeneration += 1
        this._offerDetailsLoadGeneration += 1
        RHCore.sharedInstance.procurementStore.unsubscribe(this)
        RHCore.sharedInstance.notificationStore.unsubscribe(this)
        RHCore.sharedInstance.offerStore.unsubscribe(this)
        clearInterval(this._deadlineCountdownTickTimer)
    }
    
    override async viewDidAppear() {
        await super.viewDidAppear()
        // Read-only nav badge refresh + unread-message map reload. Also picks up
        // purely time-based count changes (deadline crossings) no push covers.
        RHCore.sharedInstance.notificationStore.refreshGlobalCounts()
        RHCore.sharedInstance.notificationStore.reloadOfferMessageCounts()
        this.view.setNeedsLayout()
    }
    
    
    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.favoritesDidChange) {
            // A star was toggled (here or in another open list) — repaint the stars,
            // and when the "Lemmikud" filter is on drop/add the affected row.
            this._applyFilters()
        }
        if (event.name === RHCore.broadcastEventName.companyAffiliationsDidChange) {
            // Membership changes can enable or disable the all-companies scope.
            this._refreshCompanyScopeAffordances()
        }
    }
    
    private async _reloadData() {
        const loadGeneration = ++this._loadGeneration
        this.procurementsDataView.loading = YES
        try {
            // Every own-procurement lifecycle status. Templates stay outside the
            // lifecycle model and remain on the dedicated drafts/templates tab.
            const statuses: RHProcurementStatus[] = [
                "draft",
                "draft-cancelled",
                "published",
                "evaluation-finalized",
                "closed-no-winner",
                "closed-without-contract"
            ]
            const procurements = this._isAllCompaniesScopeActive
                ? await RHCore.sharedInstance.procurementStore.retrieveOwnProcurementsAcrossAllMyCompanies(statuses, YES)
                : await RHCore.sharedInstance.procurementStore.retrieveOwnProcurements(statuses)
            if (loadGeneration !== this._loadGeneration) {
                return
            }

            this._loadHasFailed = NO
            // Normalise publishedAt to a canonical ISO string so the data view's
            // string-based descending sort reliably means "newest published first"
            // even for legacy/test records stored with a non-ISO date string.
            // Applied recursively — a reconciliation round nested under a parent
            // row (see reconciliationRounds) sorts and displays exactly like a
            // top-level row once it's expanded into view.
            this._allProcurements = procurements.map(procurement =>
                this._normalizedProcurement(procurement)
            )
            this._applyFilters()
            this.procurementsDataView.loading = NO
            this._deadlineResolutionTrigger.reset()
            this._requestResolutionForExpiredDeadlineCountdowns()

            if (procurements.length > 0) {
                await this._reloadOfferDetails(loadGeneration)
            }
        }
        catch (error) {
            if (loadGeneration !== this._loadGeneration) {
                return
            }
            console.error("MyOngoingProcurementsViewController: error loading procurements:", error)
            this._loadHasFailed = YES
            this._allProcurements = []
            this.procurementsDataView.loading = NO
            this._applyFilters()
        }
    }

    // Top-level rows only — a reconciliation round nested under a parent row
    // (reconciliationRounds) can carry its own deadlineCountdown too, but is
    // rare enough on an already-overdue row that it's left for a follow-up
    // rather than adding recursion here.
    _requestResolutionForExpiredDeadlineCountdowns() {
        const deadlineCountdowns: { procurementId: string, deadlineDate: Date | string }[] = []
        for (const procurement of this._allProcurements) {
            const informativeStatus = procurement.procurerInformativeStatus
            if (informativeStatus?.kind !== "deadlineCountdown") {
                continue
            }
            const procurementId = procurement._id?.toString()
            if (!procurementId) {
                continue
            }
            deadlineCountdowns.push({ procurementId, deadlineDate: informativeStatus.deadlineDate })
        }
        this._deadlineResolutionTrigger.requestResolutionForExpiredDeadlines(deadlineCountdowns)
    }

    /**
     * Re-derives the visible rows from the full in-memory dataset by applying the
     * current filter criteria. The data view's own search box and column sort then
     * compose on top of this subset automatically (see CBDataView.updateTableDataByFiltering).
     * Pagination-ready: when the list is server-paged this becomes a server request
     * carrying `_filterCriteria` + the page, with no change to the panel or criteria.
     *
     * Filters apply to top-level rows only — a reconciliation round is context
     * nested inside its parent's row, not an independently filterable
     * procurement (see reconciliationRounds on RHProcurerDashboardRow),
     * so it stays with its parent whenever the parent passes.
     */
    _applyFilters() {
        this._updateChipCounts()
        const filteredProcurements = this._allProcurements.filter(procurement =>
            this._quickFilters.doesValueMatchSelection(
                procurement,
                (value, key) => this._procurementMatchesQuickFilterKey(value, key)
            ) &&
            this._procurementMatchesFilterCriteria(procurement)
        )
        // Plain `.data =` (not setTreeData) — a reconciliation round is not
        // shown as its own row here at all, expandable or otherwise. Rounds
        // stay reachable from the root's own detail/evaluation page instead
        // (see RHProcurementDetailsView._configureReconciliationField).
        this.procurementsDataView.data = filteredProcurements
        this._updateNoDataMessage()
        this.view.setNeedsLayout()
    }

    /**
     * Applies the same publishedAt/estimated-cost sort normalisation a
     * top-level row gets to a reconciliation round nested under it, and
     * recurses into any further rounds it has of its own, so every depth of
     * the tree sorts and displays consistently.
     */
    _normalizedProcurement(procurement: RHProcurerDashboardRow): RHProcurerDashboardRow {
        return {
            ...procurement,
            _rhPublishedAtSortKey: RHTimestampSorting.sortableISOString(procurement.publishedAt),
            ...RHNumberSorting.fields(RHProcurementStore.totalEstimatedCost(procurement)),
            reconciliationRounds: procurement.reconciliationRounds?.map(round =>
                this._normalizedProcurement(round)
            )
        }
    }
    
    _procuringCompanyNameForProcurement(procurement: RHProcurerDashboardRow): string {
        const company = procurement.procuringCompany
        if (company && typeof company === "object" && "name" in company) {
            return company.name ?? ""
        }
        return ""
    }

    _procurementMatchesFilterCriteria(procurement: RHProcurerDashboardRow): boolean {
        const criteria = this._filterCriteria
        if (!RHFilterPanelView.doesTextMatchCriterion(
            RHPublishedProcurementSearch.searchableTextForResponsibleProcuringUserOfProcurement(procurement),
            criteria.responsibleProcuringUser as RHTextCriterion
        )) {
            return NO
        }
        if (!RHFilterPanelView.doesTextMatchCriterion(
            this._procuringCompanyNameForProcurement(procurement),
            criteria.procuringCompanyName as RHTextCriterion
        )) {
            return NO
        }
        
        const parts = procurement.parts || []
        const costs = parts.map(part => part.estimatedCost).filter((cost): cost is number => typeof cost === "number")
        const totalCost = costs.reduce((sum, cost) => sum + cost, 0)
        if (!RHFilterPanelView.matchesNumberRange(totalCost, criteria.estimatedCost as RHRangeCriterion)) {
            return NO
        }
        
        let publishedAt: Date | undefined = undefined
        if (procurement.publishedAt) {
            publishedAt = new Date(procurement.publishedAt)
        }
        if (!RHFilterPanelView.matchesDateRange(publishedAt, criteria.publishedAt as RHDateRangeCriterion)) {
            return NO
        }
        
        let deadlineDate: Date | undefined = undefined
        if (procurement.deadlineDate) {
            deadlineDate = new Date(procurement.deadlineDate)
        }
        if (!RHFilterPanelView.matchesDateRange(deadlineDate, criteria.deadlineDate as RHDateRangeCriterion)) {
            return NO
        }
        
        const registeredCount = this._registeredTendererCountForProcurement(procurement)
        if (!RHFilterPanelView.matchesNumberRange(registeredCount, criteria.registeredTenderers as RHRangeCriterion)) {
            return NO
        }
        
        const offerCount = this._offerCountForProcurement(procurement)
        if (!RHFilterPanelView.matchesNumberRange(offerCount, criteria.offerCount as RHRangeCriterion)) {
            return NO
        }
        
        return YES
    }
    
    private _updateNoDataMessage() {
        if (this._loadHasFailed) {
            this.procurementsDataView.noDataMessage = "Andmete laadimine ebaõnnestus."
            return
        }
        
        const hasLoadedProcurements = this._allProcurements.length > 0
        const hasRowsAfterFilter = this.procurementsDataView.data.length > 0
        const hasActiveSearch = this.procurementsDataView.searchTextField.text.trim().length > 0
        const hasActiveFilter =
            Object.keys(this._filterCriteria).length > 0 ||
            this._quickFilters.selectedKeys.size > 0
        
        // The filter narrowed the loaded set to empty, or a search is active over the
        // (already filtered) subset — either way the user filtered everything out.
        if (hasLoadedProcurements && hasActiveFilter && !hasRowsAfterFilter) {
            this.procurementsDataView.noDataMessage = "Filtrile vastavaid hankeid pole"
            return
        }
        if (hasRowsAfterFilter && hasActiveSearch) {
            this.procurementsDataView.noDataMessage = "Filtrile vastavaid hankeid pole"
            return
        }
        this.procurementsDataView.noDataMessage = "Hankeid pole"
    }
    
    /**
     * Loads every submitted offer for the current procurer (one cached store
     * call) and groups them by procurement id, so the "Pakkumusi" cell can show
     * who submitted an offer and when on hover.
     */
    async _reloadOfferDetails(requiredDataLoadGeneration = this._loadGeneration) {
        const offerDetailsLoadGeneration = ++this._offerDetailsLoadGeneration
        try {
            const incomingOffers = await RHCore.sharedInstance.offerStore.getIncomingOffers()
            if (
                requiredDataLoadGeneration !== this._loadGeneration ||
                offerDetailsLoadGeneration !== this._offerDetailsLoadGeneration
            ) {
                return
            }
            const detailsByProcurement: Record<string, { companyName: string, submittedAt: string }[]> = {}
            for (const incomingOffer of incomingOffers) {
                const procurementId = incomingOffer.procurementId
                if (!procurementId) {
                    continue
                }
                const companyName = populatedValueOfReference(incomingOffer.offererCompany)?.name ?? "Tundmatu pakkuja"
                const submittedAt = incomingOffer.submittedAt?.toString() ?? ""
                if (!detailsByProcurement[procurementId]) {
                    detailsByProcurement[procurementId] = []
                }
                detailsByProcurement[procurementId].push({ companyName, submittedAt })
            }
            this._offerDetailsByProcurement = detailsByProcurement
            this.procurementsDataView.tableView.reloadData()
        }
        catch (error) {
            if (
                requiredDataLoadGeneration !== this._loadGeneration ||
                offerDetailsLoadGeneration !== this._offerDetailsLoadGeneration
            ) {
                return
            }
            console.error("MyOngoingProcurementsViewController: error loading incoming offer details:", error)
        }
    }
    
    _configureExcelExportCell(
        exportButtonView: ProcurementExcelExportButtonView,
        dataItem: RHProcurement
    ) {
        const procurementId = dataItem._id?.toString() ?? ""
        const exportData = this._excelExportsByProcurementId[procurementId]
        let state: RHProcurementExcelExportButtonState = "generate"
        
        if (this._excelExportLoadingProcurementIds.has(procurementId)) {
            state = "loading"
        }
        else if (!this._procurementDeadlineHasPassed(dataItem)) {
            state = "locked"
        }
        
        exportButtonView.configure(state, exportData?.fileName ?? "")
        exportButtonView.exportWasRequested = () => {
            if (!this._procurementDeadlineHasPassed(dataItem)) {
                CBDialogViewShower.alert("Avamisprotokolli saab koostada pärast pakkumuste esitamise tähtaega")
                return
            }
            if (exportData) {
                ProcurementExcelExportDownloader.download(exportData)
                return
            }
            this._generateExcelExportForProcurement(dataItem)
        }
    }
    
    async _generateExcelExportForProcurement(dataItem: RHProcurement) {
        const procurementId = dataItem._id?.toString()
        if (!procurementId || this._excelExportLoadingProcurementIds.has(procurementId)) {
            return
        }
        
        if (!this._procurementDeadlineHasPassed(dataItem)) {
            CBDialogViewShower.alert("Avamisprotokolli saab koostada pärast pakkumuste esitamise tähtaega")
            return
        }
        
        this._excelExportLoadingProcurementIds.add(procurementId)
        this.procurementsDataView.tableView.reloadData()
        CBDialogViewShower.showActionIndicatorDialog("Väljundit genereeritakse")
        
        let generationError: unknown
        try {
            const exportData = await RHCore.sharedInstance.procurementStore
                .exportProcurementExcel(procurementId)
            this._excelExportsByProcurementId[procurementId] = exportData
            this.procurementsDataView.tableView.reloadData()
            ProcurementExcelExportDownloader.download(exportData)
        }
        catch (error) {
            console.error("MyOngoingProcurementsViewController: error generating Excel export:", error)
            generationError = error
        }
        finally {
            this._excelExportLoadingProcurementIds.delete(procurementId)
            CBDialogViewShower.hideActionIndicatorDialog()
            this.procurementsDataView.tableView.reloadData()
        }
        
        if (generationError) {
            const errorMessage = generationError instanceof Error && generationError.message
                                 ? generationError.message
                                 : "Avamisprotokolli koostamine ebaõnnestus."
            CBDialogViewShower.alert(errorMessage)
        }
    }
    
    _procurementDeadlineHasPassed(dataItem: RHProcurement) {
        if (!dataItem.deadlineDate) {
            return NO
        }
        return new Date(dataItem.deadlineDate) <= new Date()
    }
    
    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
    }
    
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
        
        if (!this.showsPageTitle && this.titleLabel.superview === this.view) {
            this.titleLabel.removeFromSuperview()
        }
        
        bounds
            .IF(this.showsPageTitle)
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(padding, this.filterChipBar)
            .ELSE()
            .rectangleWithHeight(this.filterChipBar)
            .ENDIF()
            // The chip bar wraps its chips across as many rows as the width needs;
            // the panel (external toggle) drops in below, 0-tall while collapsed.
            .assignedAsFrameOfView(this.filterChipBar)
            .rectangleForNextRow(padding, this.filterPanel)
            .assignedAsFrameOfView(this.filterPanel)
            .rectangleForNextRow(padding, RHTables.dataViewPageHeight)
            .assignedAsFrameOfView(this.procurementsDataView)
            .rectangleForNextRow(padding, this.bottomSpacerView)
            .assignedAsFrameOfView(this.bottomSpacerView)
    }
    
}
